import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useRentalManagerUserExperimentWebApiHandler_getTreatment } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { getCookieAsBoolean } from '@zg-rentals/zillow-rental-manager/client';

import accessUtils from '../utils/accessUtils';

import { ZRM_AI_LISTING_CREATION_TEST_B_ENABLED } from '../constants/FeatureFlagConstants';
import SplitTestConstants from '../constants/SplitTestConstants';

import { useMultiplexListingFlow } from './queries/multiplex';
import { useGetListingDetails } from './queries/properties';

const CREATE_LISTING_PATH_SEGMENT = '/create-listing';

export const useCanAccessCondensedFlow = (): boolean => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  if (isInMultiplexListingFlow) return false;

  const isFeed = listingDetails?.isFeed ?? false;
  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const lastActivatedMs = listingDetails?.lastActivatedMs;
  const canAccessRelistingFlow = Boolean(listingDetails?.canBeRelisted);
  const canAccessMLReactivationFlow = listingDetails
    ? accessUtils.canAccessMLReactivationFlow({ lastActivatedMs, isFeed, isMultifamily })
    : false;

  return accessUtils.canAccessCondensedFlow({
    canAccessRelistingFlow,
    canAccessMLReactivationFlow,
    lastActivatedMs,
  });
};

export const useAiListingCreationTestBAccess = (): { isEnabled: boolean; isLoading: boolean } => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { isInMultiplexListingFlow, isLoading: isMultiplexListingFlowLoading } = useMultiplexListingFlow();

  const isFeed = listingDetails?.isFeed ?? false;
  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const lastActivatedMs = listingDetails?.lastActivatedMs;
  const canAccessRelistingFlow = Boolean(listingDetails?.canBeRelisted);
  const canAccessMLReactivationFlow = listingDetails
    ? accessUtils.canAccessMLReactivationFlow({ lastActivatedMs, isFeed, isMultifamily })
    : false;

  // Test B is the only step that collects pet policy, so flowResolver will not serve it without
  // screening criteria. Gate on it here too, or the treatment call below counts an exposure for a
  // landlord who could only ever be shown the control.
  const canAccessScreeningCriteria = listingDetails?.listingCriteria?.isEnabled ?? false;

  const isEligible =
    !isInMultiplexListingFlow &&
    !isMultiplexListingFlowLoading &&
    !isFeed &&
    canAccessScreeningCriteria &&
    Boolean(listingDetails) &&
    accessUtils.canAccessAIListingCreationTestB({
      canAccessRelistingFlow,
      canAccessMLReactivationFlow,
      lastActivatedMs,
    });

  // Read after mount: during render the cookie is false on the server, mismatching on hydration.
  const [cookie, setCookie] = useState({ checked: false, enabled: false });

  useEffect(() => {
    setCookie({ checked: true, enabled: getCookieAsBoolean(ZRM_AI_LISTING_CREATION_TEST_B_ENABLED) });
  }, []);

  const isReachable = isEligible && Boolean(router?.pathname?.includes(CREATE_LISTING_PATH_SEGMENT));
  const isQueryEnabled = isReachable && cookie.checked && !cookie.enabled;

  const { data, isPending } = useRentalManagerUserExperimentWebApiHandler_getTreatment(
    { featureName: SplitTestConstants.ZRM_AI_LISTING_CREATION_FLOW },
    {
      enabled: isQueryEnabled,
      staleTime: Infinity,
      throwOnError: false,
      meta: {
        tags: ['ai-listing-split-treatment'],
        context: {
          api: 'getTreatment',
          details: { featureName: SplitTestConstants.ZRM_AI_LISTING_CREATION_FLOW },
        },
      },
    },
  );

  if (!isReachable) {
    return { isEnabled: false, isLoading: false };
  }

  return {
    isEnabled: cookie.enabled || data?.treatment === 'on',
    isLoading: !cookie.checked || (isQueryEnabled && isPending),
  };
};

export const useCanAccessAIListingCreationTestB = (): boolean => useAiListingCreationTestBAccess().isEnabled;
